import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import React, { forwardRef, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import alertsHelper from "../../../../API/Alerts/alertsHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import AlertSettingsView from "./AlertSettingsView";
import {
  buildAlertInput,
  SEVERITY_VALUES,
  TRIGGER_TYPE_VALUES,
} from "./alertUtils";

const AlertsSettings = forwardRef(
  (
    {
      initialMapValues,
      isEdit,
      selectedId,
      backTo,
      navigateBack,
      onAlertCreated,
      onAlertDeleted,
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
      triggerTypes,
      severities,
      operators,
      workspaceUsers,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { getActiveWorkspace } = useWorkspaces();
    const savingRef = useRef(false);

    const validationSchema = AppForms.yup.object().shape({
      name: AppForms.yup.string().required().label(t("alerts.name")).max(255),
      description: AppForms.yup
        .string()
        .nullable()
        .label(t("alerts.description"))
        .max(2048),
      triggerType: AppForms.yup
        .string()
        .required()
        .oneOf(TRIGGER_TYPE_VALUES)
        .label(t("alerts.triggerType")),
      severity: AppForms.yup
        .string()
        .nullable()
        .oneOf([...SEVERITY_VALUES, null])
        .label(t("alerts.severity")),
      isActive: AppForms.yup.boolean().nullable().label(t("alerts.active")),
    });

    const handleAlertSave = async (submitted) => {
      if (savingRef.current) return;
      savingRef.current = true;
      try {
        const workspaceID = getActiveWorkspace()?.id;
        const input = buildAlertInput(submitted, workspaceID);
        let result;
        if (isEdit && selectedId) {
          result = await alertsHelper.updateAlert(selectedId, input);
          result = result?.data?.updateAlert || result;
        } else {
          result = await alertsHelper.createAlert(input);
          result = result?.data?.createAlert || result;
        }
        onAlertCreated && onAlertCreated("success", isEdit, result?.id, submitted);
      } catch (error) {
        console.error(error);
        onAlertCreated && onAlertCreated("error", isEdit, null, submitted);
      } finally {
        savingRef.current = false;
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                }
          }
        >
          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleAlertSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <AlertSettingsView
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
              workspaceUsers={workspaceUsers}
              triggerTypes={triggerTypes}
              severities={severities}
              operators={operators}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        initialMapValues,
        isEdit,
        selectedId,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
        workspaceUsers,
        triggerTypes,
        severities,
        operators,
      ],
    );
    return _view;
  },
);

export default AlertsSettings;
